import axios from '@/lib/axios'
import { buildAdminRequestConfig, type OrganizationId } from '@/lib/organization-scoping'
import type { CartPaymentActionResponse } from '@/lib/services/carts/carts.types'
import type {
  CancelledReaderAction,
  DeletedTerminal,
  RegisterTerminalInput,
  TerminalAccountContext,
  TerminalReader,
  TerminalReaders,
  UpdateTerminalInput,
} from './terminals.types'

interface AccountScope {
  organizationId: OrganizationId
  accountContext?: TerminalAccountContext | undefined
}

interface TerminalScope extends AccountScope {
  terminalId: string
}

function accountConfig(organizationId: OrganizationId, accountContext?: TerminalAccountContext) {
  return buildAdminRequestConfig({
    organizationId,
    ...(accountContext ? { params: { account_context: accountContext } } : {}),
  })
}

export async function getTerminals({ organizationId, accountContext }: AccountScope) {
  return await axios.get<TerminalReaders>(
    '/stripe-terminal/readers',
    accountConfig(organizationId, accountContext)
  )
}

export async function getTerminal({ organizationId, terminalId, accountContext }: TerminalScope) {
  return axios.get<TerminalReader>(
    `/stripe-terminal/readers/${terminalId}`,
    accountConfig(organizationId, accountContext)
  )
}

export async function registerTerminal({
  organizationId,
  accountContext,
  ...data
}: AccountScope & RegisterTerminalInput) {
  return axios.post<TerminalReader>(
    '/stripe-terminal/readers',
    data,
    accountConfig(organizationId, accountContext)
  )
}

export async function updateTerminal({
  organizationId,
  terminalId,
  accountContext,
  ...data
}: TerminalScope & UpdateTerminalInput) {
  return axios.put<TerminalReader>(
    `/stripe-terminal/readers/${terminalId}`,
    data,
    accountConfig(organizationId, accountContext)
  )
}

export async function deleteTerminal({
  organizationId,
  terminalId,
  accountContext,
  retire,
}: TerminalScope & { retire?: boolean | undefined }) {
  return axios.delete<DeletedTerminal>(
    `/stripe-terminal/readers/${terminalId}`,
    buildAdminRequestConfig({
      organizationId,
      params: {
        ...(accountContext ? { account_context: accountContext } : {}),
        ...(retire ? { retire: 1 } : {}),
      },
    })
  )
}

export interface SendCardPaymentToReaderInput {
  cartId: number
  terminalId: string
  amount?: number | undefined
  customerTotal?: number | undefined
  /** Names this submission: sending it again under the same key gets the payment the server already sent. */
  idempotencyKey?: string | undefined
  organizationWorkstationId?: number | null | undefined
  organizationWorkstationClaim?: string | null | undefined
}

export async function sendCardPaymentToReader({
  organizationId,
  cartId,
  terminalId,
  amount,
  customerTotal,
  idempotencyKey,
  organizationWorkstationId,
  organizationWorkstationClaim,
}: { organizationId: OrganizationId } & SendCardPaymentToReaderInput) {
  return axios.post<CartPaymentActionResponse>(
    `/stripe-terminal/readers/${terminalId}/cart/${cartId}/send-card-payment`,
    {
      ...(amount !== undefined ? { amount } : {}),
      ...(customerTotal !== undefined ? { customer_total: customerTotal } : {}),
      ...(idempotencyKey ? { idempotency_key: idempotencyKey } : {}),
      ...(organizationWorkstationId !== undefined
        ? {
            organization_workstation_id: organizationWorkstationId,
            organization_workstation_claim: organizationWorkstationClaim ?? null,
          }
        : {}),
    },
    buildAdminRequestConfig({ organizationId })
  )
}

export async function cancelReaderAction({
  organizationId,
  terminalId,
  accountContext,
}: TerminalScope) {
  return axios.post<CancelledReaderAction>(
    `/stripe-terminal/readers/${terminalId}/cancel-action`,
    null,
    accountConfig(organizationId, accountContext)
  )
}
